import {
  isWorkbookErrorCell,
  type WorkbookCell,
  type WorkbookColumnarSheet,
  type WorkbookTable,
} from "@erp/shared-types";
import type { ColumnType } from "./formula";
import type { SheetPlan } from "./planTypes";

type SortRule = WorkbookColumnarSheet["sort"][number];
type SortKey = { index: number; type: ColumnType; descending: boolean };

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/** Text in DuckDB's binary collation: UTF-8 byte order, which is code point order. */
function compareText(left: string, right: string): number {
  const a = left[Symbol.iterator]();
  const b = right[Symbol.iterator]();
  for (;;) {
    const x = a.next();
    const y = b.next();
    if (x.done || y.done) return Number(!x.done) - Number(!y.done);
    const difference =
      (x.value.codePointAt(0) ?? 0) - (y.value.codePointAt(0) ?? 0);
    if (difference) return difference;
  }
}

/** Exact comparison of plain decimal strings, such as DuckDB's DECIMAL text. */
function compareDecimal(left: RegExpExecArray, right: RegExpExecArray): number {
  const sign = (match: RegExpExecArray) => {
    const zero = /^0*$/.test(`${match[2]}${match[3] ?? ""}`);
    return zero ? 0 : match[1] ? -1 : 1;
  };
  const leftSign = sign(left);
  const rightSign = sign(right);
  if (leftSign !== rightSign || leftSign === 0) return leftSign - rightSign;
  const whole = (match: RegExpExecArray) =>
    (match[2] ?? "").replace(/^0+(?=\d)/, "");
  const fraction = (match: RegExpExecArray, length: number) =>
    (match[3] ?? "").padEnd(length, "0");
  const length = Math.max(left[3]?.length ?? 0, right[3]?.length ?? 0);
  const a = whole(left);
  const b = whole(right);
  const magnitude =
    a.length - b.length ||
    compareText(a, b) ||
    compareText(fraction(left, length), fraction(right, length));
  return leftSign * magnitude;
}

/**
 * Compares two non-null Result values as the engine orders their Declared
 * type. `readable` is false when a value does not read as that type, in which
 * case they are compared as text.
 */
function compareValues(
  left: WorkbookCell,
  right: WorkbookCell,
  type: ColumnType,
): { order: number; readable: boolean } {
  const a = String(left);
  const b = String(right);
  if (type === "decimal") {
    const x = DECIMAL.exec(a);
    const y = DECIMAL.exec(b);
    if (x && y) return { order: compareDecimal(x, y), readable: true };
    return { order: compareText(a, b), readable: false };
  }
  if (type === "boolean") {
    const readable = [a, b].every(
      (value) => value === "true" || value === "false",
    );
    return { order: compareText(a, b), readable };
  }
  // ISO dates, and ISO dates and times in UTC, order as text.
  const pattern =
    type === "date" ? DATE : type === "datetime" ? DATETIME : undefined;
  const readable = !pattern || (pattern.test(a) && pattern.test(b));
  return { order: compareText(a, b), readable };
}

/**
 * The sort keys the Preview can apply: each rule up to the first whose column
 * is not displayed in `table` or has no Declared type. Sorting by that prefix
 * never reorders rows the engine already put in order.
 */
function sortKeys(
  table: WorkbookTable,
  plan: SheetPlan,
  sort: readonly SortRule[],
): SortKey[] {
  const keys: SortKey[] = [];
  for (const rule of sort) {
    const index = table.columns.indexOf(rule.column);
    const type = plan.output.find((field) => field.name === rule.column)?.type;
    if (index < 0 || !type) break;
    keys.push({ index, type, descending: rule.direction === "desc" });
  }
  return keys;
}

/** Where a cell sorts whatever the direction: values, then Error cells, then blanks. */
function placement(cell: WorkbookCell): number {
  if (cell === null) return 2;
  return isWorkbookErrorCell(cell) ? 1 : 0;
}

/** Compares two cells by one sort key; Error cells and then nulls sort last whatever the direction. */
function compareCells(
  left: WorkbookCell,
  right: WorkbookCell,
  key: SortKey,
): { order: number; readable: boolean } {
  const placed = placement(left) - placement(right);
  // Error cells tie with each other and keep the table's order, as blanks do.
  if (placed || left === null || right === null || placement(left))
    return { order: placed, readable: true };
  const compared = compareValues(left, right, key.type);
  return {
    order: key.descending ? -compared.order : compared.order,
    readable: compared.readable,
  };
}

/** Compares two rows key by key. */
function compareRows(
  a: readonly WorkbookCell[],
  b: readonly WorkbookCell[],
  keys: readonly SortKey[],
): { order: number; readable: boolean } {
  let readable = true;
  for (const key of keys) {
    const compared = compareCells(
      a[key.index] ?? null,
      b[key.index] ?? null,
      key,
    );
    readable &&= compared.readable;
    if (compared.order) return { order: compared.order, readable };
  }
  return { order: 0, readable };
}

/**
 * Re-sorts a Result's Preview rows by `sort` as the engine orders them:
 * each key by its Declared type, Error cells and then nulls last in either
 * direction, ties kept in the table's order. The order is `exact` only when every sort key is a
 * displayed Output column and every value reads as its Declared type; a key
 * the Preview cannot see (an input-only Field) stops the local sort there.
 */
export function sortPreviewRows(
  table: WorkbookTable,
  plan: SheetPlan,
  sort: readonly SortRule[],
): { rows: WorkbookCell[][]; exact: boolean } {
  const keys = sortKeys(table, plan, sort);
  let exact = keys.length === sort.length;
  const rows = [...table.rows].sort((a, b) => {
    const { order, readable } = compareRows(a, b, keys);
    exact &&= readable;
    return order;
  });
  return { rows, exact };
}

/**
 * A Suggested type guessed from sample values. It only seeds Add Column's
 * default; Source columns are text to the engine whatever their values look
 * like. Codes with leading zeros stay text.
 */
export function suggestColumnType(values: readonly WorkbookCell[]): ColumnType {
  const present = values.filter(
    (value) => value !== null && value !== undefined && value !== "",
  );
  if (!present.length) return "text";
  if (present.every((value) => typeof value === "boolean")) return "boolean";
  if (
    present.every(
      (value) =>
        typeof value === "number" ||
        (typeof value === "string" &&
          /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value)),
    )
  )
    return "decimal";
  return "text";
}
